import { useContext, useEffect, useRef } from 'react';

import ValidationContext from './ValidationContext.ts';

export type NoError = false;

export type ErrorMessage = string;

export type Errors = ErrorMessage[] | React.ReactNode[];
export type ErrorState = NoError | Errors;

/**
 * Returns whether the form is currently valid
 */
export type IsCurrentlyValid = () => boolean;

/**
 * Allows a component to register with a form for ensuring that a form is valid before proceeding with an action or step
 * @param isValid Whether the component is considered to be in valid state for the form
 */
export default function useFormValidation(isValid?: boolean): IsCurrentlyValid {
  const registeredId = useRef<string | null>(null);
  const { register, deregister, update, getIsValid } = useContext(ValidationContext);

  useEffect(() => {
    if (registeredId.current === null) {
      registeredId.current = register(isValid);
    }

    return () => deregister(registeredId.current!);
  }, [register, deregister, registeredId, isValid]);
  useEffect(() => {
    if (registeredId.current) {
      // hasError has changed so tell the form
      update(registeredId.current, isValid);
    }
  }, [isValid, registeredId, update]);

  return getIsValid;
}
